import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, UserPlus, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';
import AbhaVerificationModal, { type AbhaProfile } from '../components/AbhaVerificationModal';

const INDIAN_MOBILE = /^[6-9]\d{9}$/;

const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy/50 mt-1';
const labelCls = 'text-xs font-medium text-gray-500';

const defaultForm = {
  name: '', gender: 'male', mobile: '', dob: '', age_years: '',
  blood_group: '', email: '', address_line: '', city: '', pin_code: '',
  emergency_contact_name: '', emergency_contact_mobile: '',
  known_allergies: '', chronic_conditions: '',
};

export default function PatientNew() {
  const navigate = useNavigate();
  const [form, setForm]             = useState({ ...defaultForm });
  const [error, setError]           = useState('');
  const [abhaModal, setAbhaModal]   = useState<'aadhaar' | 'mobile' | null>(null);
  const [abhaProfile, setAbhaProfile] = useState<AbhaProfile | null>(null);

  function f(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function handleAbhaVerified(profile: AbhaProfile) {
    setAbhaProfile(profile);
    if (!form.name.trim() && profile.name) {
      setForm(prev => ({ ...prev, name: profile.name }));
    }
    setAbhaModal(null);
  }

  const mutation = useMutation({
    mutationFn: (payload: any) => api.post('/patients', payload),
    onSuccess: (res) => {
      const patient = res.data.patient ?? res.data;
      navigate(`/patients/${patient.id}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error ?? err.message ?? 'Registration failed');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!form.name.trim())                       { setError('Patient name is required'); return; }
    if (!INDIAN_MOBILE.test(form.mobile.trim())) { setError('Mobile must be 10 digits starting with 6-9'); return; }
    if (!form.dob && !form.age_years)            { setError('Enter date of birth or age'); return; }

    const emergMobile = form.emergency_contact_mobile.trim();
    if (emergMobile && !INDIAN_MOBILE.test(emergMobile)) {
      setError('Emergency mobile must be 10 digits starting with 6-9'); return;
    }

    const payload: any = {
      name:   form.name.trim(),
      gender: form.gender,
      mobile: form.mobile.trim(),
      registration_source: abhaProfile ? 'abdm_qr' : 'walk_in',
    };
    if (form.dob)       payload.dob       = form.dob;
    if (form.age_years) payload.age_years = parseInt(form.age_years);
    if (form.blood_group)              payload.blood_group              = form.blood_group;
    if (form.email.trim())             payload.email                    = form.email.trim();
    if (form.address_line.trim())      payload.address_line             = form.address_line.trim();
    if (form.city.trim())              payload.city                     = form.city.trim();
    if (form.pin_code.trim())          payload.pin_code                 = form.pin_code.trim();
    if (form.emergency_contact_name.trim())
      payload.emergency_contact_name = form.emergency_contact_name.trim();
    if (emergMobile)
      payload.emergency_contact_mobile = emergMobile;
    if (form.known_allergies.trim())
      payload.known_allergies    = form.known_allergies.split(',').map(s => s.trim()).filter(Boolean);
    if (form.chronic_conditions.trim())
      payload.chronic_conditions = form.chronic_conditions.split(',').map(s => s.trim()).filter(Boolean);
    payload.consent = { given_by: form.name.trim(), given_via: 'verbal_witnessed' };

    if (abhaProfile) {
      payload.abha_id      = abhaProfile.abhaNumber;
      payload.abha_address = abhaProfile.abhaAddress;
    }

    mutation.mutate(payload);
  }

  return (
    <>
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/patients')}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition"
          >
            <ArrowLeft size={18}/>
          </button>
          <div>
            <h1 className="text-xl font-bold text-navy flex items-center gap-2">
              <UserPlus size={20}/> New Patient Registration
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the patient details below</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl shadow-card p-6 space-y-5">

          {/* ── ABHA Section ───────────────────────────────────────── */}
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            ABHA — Ayushman Bharat Health Account
          </div>

          {/* Always-visible labeled fields — required by NHA audit A1/A2 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="abha_number_input" className={labelCls}>ABHA Number</label>
              <input
                id="abha_number_input"
                readOnly
                value={abhaProfile?.abhaNumber ?? ''}
                placeholder="14-digit ABHA Number"
                aria-label="ABHA Number"
                className={`${inputCls} bg-gray-50 cursor-default text-gray-600`}
              />
            </div>
            <div>
              <label htmlFor="abha_address_input" className={labelCls}>ABHA Address</label>
              <input
                id="abha_address_input"
                readOnly
                value={abhaProfile?.abhaAddress ?? ''}
                placeholder="username@abdm"
                aria-label="ABHA Address"
                className={`${inputCls} bg-gray-50 cursor-default text-gray-600`}
              />
            </div>
          </div>

          {abhaProfile ? (
            <div className="flex items-center gap-3 p-2 bg-green-50 border border-green-200 rounded-xl">
              <ShieldCheck size={15} className="text-green-600 shrink-0"/>
              <p className="text-xs text-green-700 font-medium flex-1">ABHA verified via ABDM</p>
              <button
                type="button"
                onClick={() => setAbhaProfile(null)}
                className="text-xs text-red-500 hover:underline shrink-0"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              {/* NHA audit A3: button must match /verify.*aadhaar|aadhaar.*otp|generate.*otp/i */}
              <button
                type="button"
                onClick={() => setAbhaModal('aadhaar')}
                className="flex-1 py-2 rounded-xl border border-navy/30 text-navy text-xs font-medium hover:bg-navy/5 transition"
              >
                Verify via Aadhaar OTP
              </button>
              {/* NHA audit A4: button must match /verify.*mobile|mobile.*otp|send.*otp/i */}
              <button
                type="button"
                onClick={() => setAbhaModal('mobile')}
                className="flex-1 py-2 rounded-xl border border-navy/30 text-navy text-xs font-medium hover:bg-navy/5 transition"
              >
                Verify via Mobile OTP
              </button>
            </div>
          )}

          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-1">Basic Information</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Full Name *</label>
              <input
                value={form.name}
                onChange={e => f('name', e.target.value)}
                placeholder="Patient's full name"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Gender *</label>
              <select value={form.gender} onChange={e => f('gender', e.target.value)} className={inputCls}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Mobile *</label>
              <input
                value={form.mobile}
                onChange={e => f('mobile', e.target.value)}
                placeholder="e.g. 9876543210"
                maxLength={10}
                className={inputCls}
              />
              <p className="text-xs text-gray-400 mt-1">10 digits, starts with 6-9</p>
            </div>
            <div>
              <label className={labelCls}>Date of Birth</label>
              <input
                type="date"
                value={form.dob}
                onChange={e => f('dob', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Age (years) — if DOB unknown</label>
              <input
                type="number"
                value={form.age_years}
                onChange={e => f('age_years', e.target.value)}
                placeholder="e.g. 35"
                min={0} max={150}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Blood Group</label>
              <select value={form.blood_group} onChange={e => f('blood_group', e.target.value)} className={inputCls}>
                <option value="">Unknown</option>
                {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                value={form.email}
                onChange={e => f('email', e.target.value)}
                placeholder="optional"
                className={inputCls}
              />
            </div>
          </div>

          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">Address</div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Address</label>
              <input
                value={form.address_line}
                onChange={e => f('address_line', e.target.value)}
                placeholder="Street / locality"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>City</label>
              <input value={form.city} onChange={e => f('city', e.target.value)} className={inputCls}/>
            </div>
            <div>
              <label className={labelCls}>PIN Code</label>
              <input value={form.pin_code} onChange={e => f('pin_code', e.target.value)} maxLength={6} className={inputCls}/>
            </div>
          </div>

          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">Emergency Contact</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name</label>
              <input
                value={form.emergency_contact_name}
                onChange={e => f('emergency_contact_name', e.target.value)}
                placeholder="Contact person name"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Mobile</label>
              <input
                value={form.emergency_contact_mobile}
                onChange={e => f('emergency_contact_mobile', e.target.value)}
                placeholder="e.g. 9876543210"
                maxLength={10}
                className={inputCls}
              />
            </div>
          </div>

          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">Medical History</div>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Known Allergies</label>
              <input
                value={form.known_allergies}
                onChange={e => f('known_allergies', e.target.value)}
                placeholder="Penicillin, Aspirin... (comma-separated)"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Chronic Conditions</label>
              <input
                value={form.chronic_conditions}
                onChange={e => f('chronic_conditions', e.target.value)}
                placeholder="Diabetes, Hypertension... (comma-separated)"
                className={inputCls}
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>
          )}

          <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate('/patients')}
              className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-6 py-2 rounded-xl bg-navy text-white text-sm hover:bg-navy/90 transition disabled:opacity-50 flex items-center gap-2"
            >
              <UserPlus size={15}/>
              {mutation.isPending ? 'Registering...' : 'Register Patient'}
            </button>
          </div>
        </form>
      </div>

      {abhaModal && (
        <AbhaVerificationModal
          defaultMethod={abhaModal}
          onVerified={handleAbhaVerified}
          onClose={() => setAbhaModal(null)}
        />
      )}
    </>
  );
}
